import React from "react";
import type { AuditLogChanges, AuditLogJsonValue } from "../types";

/* ──────────────────────────────────────────────────────────────────────────────
 * Helpers
 * ──────────────────────────────────────────────────────────────────────────── */

function isPrimitive(value: AuditLogJsonValue | undefined): boolean {
  return (
    value === null ||
    value === undefined ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  );
}

function deepEqual(a: AuditLogJsonValue | undefined, b: AuditLogJsonValue | undefined): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a !== typeof b) return false;
  if (typeof a !== "object") return false;
  // Cheap structural compare — payloads from the audit-log API are tiny
  // (a single resource snapshot), so JSON.stringify is fine here.
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

function formatPrimitive(value: AuditLogJsonValue | undefined): string {
  if (value === null) return "null";
  if (value === undefined) return "—";
  if (typeof value === "boolean") return value ? "true" : "false";
  return String(value);
}

function prettyJson(value: AuditLogJsonValue): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

/* ──────────────────────────────────────────────────────────────────────────────
 * Atomic value renderers
 * ──────────────────────────────────────────────────────────────────────────── */

interface ValueProps {
  value: AuditLogJsonValue | undefined;
  tone: "before" | "after" | "neutral";
}

const ValueCell: React.FC<ValueProps> = ({ value, tone }) => {
  const toneClass =
    tone === "before"
      ? "bg-red-50 text-red-800 line-through decoration-red-400"
      : tone === "after"
      ? "bg-green-50 text-green-800"
      : "bg-semantic-bg-secondary text-semantic-text-primary";

  if (value === undefined) {
    return (
      <span
        className={`inline-block min-h-[1.5rem] rounded px-2 py-1 text-xs italic text-semantic-text-tertiary`}
      >
        —
      </span>
    );
  }

  if (isPrimitive(value)) {
    return (
      <span
        className={`inline-block min-h-[1.5rem] rounded px-2 py-1 font-mono text-xs ${toneClass}`}
      >
        {formatPrimitive(value)}
      </span>
    );
  }

  return (
    <pre
      className={`max-h-64 overflow-auto whitespace-pre-wrap break-words rounded px-2 py-1 font-mono text-xs ${toneClass}`}
    >
      {prettyJson(value)}
    </pre>
  );
};

/* ──────────────────────────────────────────────────────────────────────────────
 * Single-payload (created / deleted) view
 * ──────────────────────────────────────────────────────────────────────────── */

interface SinglePayloadProps {
  payload: Record<string, AuditLogJsonValue>;
  tone: "before" | "after";
  title: string;
}

const SinglePayloadView: React.FC<SinglePayloadProps> = ({
  payload,
  tone,
  title,
}) => {
  const entries = Object.entries(payload);
  if (entries.length === 0) {
    return (
      <p className="text-sm italic text-semantic-text-secondary">
        {title} (no fields recorded)
      </p>
    );
  }
  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-semantic-text-secondary">
        {title}
      </p>
      <div className="overflow-hidden rounded-md border border-semantic-border-secondary">
        <table className="w-full table-fixed text-left text-sm">
          <colgroup>
            <col style={{ width: "30%" }} />
            <col />
          </colgroup>
          <thead className="bg-semantic-bg-secondary">
            <tr>
              <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-semantic-text-secondary">
                Field
              </th>
              <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-semantic-text-secondary">
                Value
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-semantic-border-secondary">
            {entries.map(([key, val]) => (
              <tr key={key} className="align-top">
                <td className="px-3 py-2 font-mono text-xs text-semantic-text-primary">
                  {key}
                </td>
                <td className="px-3 py-2">
                  <ValueCell value={val} tone={tone} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────────────────────
 * Side-by-side diff view
 * ──────────────────────────────────────────────────────────────────────────── */

interface SideBySideDiffProps {
  before: Record<string, AuditLogJsonValue>;
  after: Record<string, AuditLogJsonValue>;
}

const SideBySideDiff: React.FC<SideBySideDiffProps> = ({ before, after }) => {
  // Union of keys, preserving original order: `before` keys first, then any
  // new keys from `after` that weren't present.
  const seen = new Set<string>();
  const orderedKeys: string[] = [];
  for (const k of Object.keys(before)) {
    if (!seen.has(k)) {
      seen.add(k);
      orderedKeys.push(k);
    }
  }
  for (const k of Object.keys(after)) {
    if (!seen.has(k)) {
      seen.add(k);
      orderedKeys.push(k);
    }
  }

  if (orderedKeys.length === 0) {
    return (
      <p className="text-sm italic text-semantic-text-secondary">
        No field-level changes recorded.
      </p>
    );
  }

  return (
    <div className="overflow-hidden rounded-md border border-semantic-border-secondary">
      <table className="w-full table-fixed text-left text-sm">
        <colgroup>
          <col style={{ width: "22%" }} />
          <col />
          <col />
        </colgroup>
        <thead className="bg-semantic-bg-secondary">
          <tr>
            <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-semantic-text-secondary">
              Field
            </th>
            <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-semantic-text-secondary">
              Before
            </th>
            <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-semantic-text-secondary">
              After
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-semantic-border-secondary">
          {orderedKeys.map((key) => {
            const beforeVal = before[key];
            const afterVal = after[key];
            const equal = deepEqual(beforeVal, afterVal);
            const beforeMissing = !(key in before);
            const afterMissing = !(key in after);
            const rowClass = equal
              ? "bg-semantic-bg-primary"
              : "bg-amber-50/40";

            return (
              <tr key={key} className={`align-top ${rowClass}`}>
                <td className="px-3 py-2 font-mono text-xs text-semantic-text-primary">
                  <div className="flex items-center gap-2">
                    <span>{key}</span>
                    {!equal && (
                      <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                        changed
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <ValueCell
                    value={beforeMissing ? undefined : beforeVal}
                    tone={equal ? "neutral" : "before"}
                  />
                </td>
                <td className="px-3 py-2">
                  <ValueCell
                    value={afterMissing ? undefined : afterVal}
                    tone={equal ? "neutral" : "after"}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────────────────────
 * Public component
 * ──────────────────────────────────────────────────────────────────────────── */

interface AuditLogDiffViewProps {
  changes: AuditLogChanges | null | undefined;
}

export const AuditLogDiffView: React.FC<AuditLogDiffViewProps> = ({
  changes,
}) => {
  if (!changes) {
    return (
      <p className="text-sm italic text-semantic-text-secondary">
        No changes payload recorded for this entry.
      </p>
    );
  }

  const hasBefore = changes.before != null && Object.keys(changes.before).length > 0;
  const hasAfter = changes.after != null && Object.keys(changes.after).length > 0;

  if (hasBefore && hasAfter) {
    return (
      <SideBySideDiff
        before={changes.before as Record<string, AuditLogJsonValue>}
        after={changes.after as Record<string, AuditLogJsonValue>}
      />
    );
  }

  if (hasAfter) {
    return (
      <SinglePayloadView
        payload={changes.after as Record<string, AuditLogJsonValue>}
        tone="after"
        title="Created with"
      />
    );
  }

  if (hasBefore) {
    return (
      <SinglePayloadView
        payload={changes.before as Record<string, AuditLogJsonValue>}
        tone="before"
        title="Deleted, last value"
      />
    );
  }

  return (
    <p className="text-sm italic text-semantic-text-secondary">
      No fields recorded in this change.
    </p>
  );
};

export default AuditLogDiffView;
